import React, { useMemo } from 'react';
import PageWrapper from '../components/PageWrapper';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Container } from '@mui/material';
import { PaidOrderProduct, PendingOrderProduct, useRetrievePaidOrderQuery } from '../services/orders';
import { QuickOrderCombinedTotal } from './orders/components/OrderPaymentTotals';
import { Currencies, OrderStatus2 } from '../types';
import { useTranslation } from 'react-i18next';
import ReactPixel from 'react-facebook-pixel';
import { useAuthEffects } from '../components/Auth';
import { ProductDocument } from './products/types';
import { FileItem } from '../components/FileItem';
import { CheckoutOrderProducts } from './checkout/CheckoutOrderProducts';

const OrderDigitalProducts = ({ orderDocuments }: { orderDocuments: ProductDocument[] }) => {
	const { t } = useTranslation();
	return <Box px={2}>
		<Typography variant='body2'>
			{t('paymentSuccess.digitalContentReady')}
		</Typography>
		<Box display="flex" flexDirection="row" gap={1} flexWrap="wrap" my={1}>
			{orderDocuments.map((document: ProductDocument) => (
				<FileItem key={document.id} document={document} />
			))}
		</Box>
	</Box>;
};

export const PaymentSuccessView = () => {
	useAuthEffects();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const paymentIntentId = searchParams.get('payment_intent') as string;
	const clientSecret = searchParams.get('payment_intent_client_secret') as string;
	const [shouldPoll, setShouldPoll] = useState(true);
	const [retryCount, setRetryCount] = useState(0);
	const [shouldShowError, setShouldShowError] = useState(false);
	const MAX_RETRIES = 10;

	const {data: paidOrderResult, isLoading} = useRetrievePaidOrderQuery({
		paymentIntentId,
		clientSecret
	}, {pollingInterval: 1000, refetchOnMountOrArgChange: false, skip: !paymentIntentId || !clientSecret || !shouldPoll});

	const paidOrder = useMemo(() => paidOrderResult?.order ?? null, [paidOrderResult]);
	const additionalOrder = useMemo(() => paidOrderResult?.combinedOrder ?? null, [paidOrderResult]);

	const redirectToHome = () => {
		navigate('/');
	};

	useEffect(() => {
		if (paidOrder && paidOrder.status === OrderStatus2.Paid) {
			localStorage.removeItem('orderItems');
			localStorage.removeItem('paymentIntent');
			ReactPixel.track('Purchase', {
				value: (Number(paidOrder.total)/100).toFixed(2),
				currency: paidOrder?.currency,
			});
		}
	}, [paidOrder]);

	useEffect(() => {
		if (!isLoading && !paidOrder) {
			redirectToHome();
		}
	}, [isLoading]);

	useEffect(() => {
		if (!paymentIntentId || !clientSecret) {
			redirectToHome();
		}
	}, [paymentIntentId, clientSecret]);

	useEffect(() => {
		if ((!paidOrder || paidOrder.status !== OrderStatus2.Paid) && shouldPoll) {
			const timer = setTimeout(() => {
				if (retryCount >= MAX_RETRIES - 1) {
					setShouldPoll(false);
					setShouldShowError(true);
				} else {
					setRetryCount(prev => prev + 1);
				}
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [retryCount, shouldPoll, paidOrder]);

	useEffect(() => {
		if (paidOrder && paidOrder.status === OrderStatus2.Paid) {
			setShouldPoll(false);
			localStorage.removeItem('pendingOrderId');
			localStorage.removeItem('paymentIntentClientSecret');
		}
	}, [paidOrder]);

	if (shouldShowError) {
		return <PageWrapper noScroll>
			<Box flex={1} display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2} p={4}>
				<Box>
					<Typography variant="h4">Couldn&apos;t process payment</Typography>
				</Box>
				<Box>
					<Typography variant="h6">Please check your email</Typography>
				</Box>
			</Box>
		</PageWrapper>;
	}

	const paidOrderDocuments = paidOrder?.products?.flatMap((product: PendingOrderProduct) => (product as PaidOrderProduct).documents) ?? [];
	const additionalOrderDocuments = additionalOrder?.products?.flatMap((product: PendingOrderProduct) => (product as PaidOrderProduct).documents) ?? [];
	const totalProducts = [paidOrder?.products ?? [], additionalOrder?.products ?? []].flat();
	const totalAdditionalFees = [paidOrder?.additionalFees ?? [], additionalOrder?.additionalFees ?? []].flat();
	const isCombinedOrder = Boolean(additionalOrder);

	return <PageWrapper maxWidth={false} disableGutters isLoading={!paidOrder || paidOrder.status !== 'paid'} loadingMessage="Processing...">
		{paidOrder && <div>
			<Container maxWidth='sm'>
				<Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2} py={8}>
					<Typography variant="h4">{t('paymentSuccess.title')}</Typography>
					<Typography variant="h6">{t('paymentSuccess.orderId', {orderId: paidOrder?.id})}</Typography>
				</Box>
				<Typography variant='h6' sx={{ my: 2 }} textAlign='center'>{t('checkout.products')}</Typography>
				<Box display='flex' flexDirection='column' gap={2}>
					{additionalOrder && <CheckoutOrderProducts orderInfo={additionalOrder} products={additionalOrder.products} additionalFees={additionalOrder.additionalFees} orderIdx={isCombinedOrder ? 0 : undefined} />}
					{Boolean(additionalOrderDocuments.length) && <OrderDigitalProducts orderDocuments={additionalOrderDocuments} />}
					{paidOrder && <CheckoutOrderProducts orderInfo={paidOrder} products={paidOrder.products} additionalFees={paidOrder.additionalFees} orderIdx={additionalOrder ? 1 : undefined} />}
					{Boolean(paidOrderDocuments.length) && <OrderDigitalProducts orderDocuments={paidOrderDocuments} />}
				</Box>
				{isCombinedOrder && <QuickOrderCombinedTotal 
					products={totalProducts}
					additionalFees={totalAdditionalFees}
					currency={paidOrder?.currency as Currencies}
					showCheckmark={true}
				/>}
			</Container>
		</div>}
	</PageWrapper>;
};

